import Vue from 'vue';
import { Module } from 'vuex';
import { RootState } from '@/types/store';
import { CityDetailModel } from '../models/cityData';
import atlas, { DreamAtlasState } from './atlas';
import { getSessionStorage } from '@/lib/service/storage';

export interface DreamState {
    infoMap: {
        [activityId: string]: Dream.Info;
    };
    cityListMap: {
        [activityId: string]: Dream.CityItem[];
    };
    cityDetailMap: {
        [cityId: string]: CityDetailModel;
    };
    landmarkMap: {
        [landmarkId: number]: Dream.CityLandmark<number>,
    };
    landmarkDetailMap: {
        [landmarkId: string]: Dream.LandmarkDetail;
    };
    messageList: ListWrap<Dream.Messsage> & ListRequesting;
    participationList: {
        [activityId: string]: ListWrap<Dream.ParticipationItem> & ListRequesting;
    };
    energy: number;

    landmarkRank: ListWrap<Dream.RankItem> & ListRequesting & {
        self: Dream.RankItem | null;
    };
    jigsawRank: ListWrap<Dream.RankItem> & ListRequesting & {
        self: Dream.RankItem | null;
    };
    touringRank: ListWrap<Dream.RankItem> & ListRequesting & {
        self: Dream.RankItem | null;
    };
    isNew: boolean;
    finishCount: Dream.FinishCount;
    missions: Dream.MissionItem[];
    history: ListWrap<Dream.History> & ListReq;

    atlas: DreamAtlasState;
    tipsAlert: Dream.TipsAlert;
    expire: Dream.Expire;
    rollMsg: Dream.RollMsg;
    medalCard: string;
    specialActive: Dream.SpecialActive | null;
    specialTeam: ListWrap<IdName> & ListReq;
    specialOption: null | { cityId: number; teamId: number; specialId: number };
    inviteInfoMap: {
        [activityId: number]: Dream.InviteInfo | null,
    };
    currentCityID: number;
    cityMapFinishData: {
        [cityId: string]: Dream.GoFinish,
    };
    imgloadMap: {
        [key: string]: { [key: string]: HTMLImageElement; };
    };
    noticeSwitch: Dream.NoticeSwitch;
}

const m: Module<DreamState, RootState> = {
    namespaced: true,
    // @ts-ignore
    state: {
        infoMap: {},
        cityListMap: {},
        cityDetailMap: {},
        landmarkDetailMap: {},
        landmarkMap: {},
        messageList: {
            list: [],
            loaded: false,
            requesting: false,
        },
        participationList: {},
        energy: 0,

        landmarkRank: {
            list: [],
            loaded: false,
            requesting: false,
            self: null,
        },

        jigsawRank: {
            list: [],
            loaded: false,
            requesting: false,
            self: null,
        },

        touringRank: {
            list: [],
            loaded: false,
            requesting: false,
            self: null,
        },
        isNew: false,
        finishCount: {},
        missions: [],
        history: {
            list: [],
            loaded: false,
            req: false,
        },
        medalCard: '',
        specialActive: null,
        specialTeam: {
            list: [],
            loaded: false,
            req: false,
        },
        specialOption: null,
        inviteInfoMap: {},
        currentCityID: 0,
        cityMapFinishData: {},
        imgloadMap: {},
        noticeSwitch: {
            go_notice: true,
            visit_notice: true,
        },
    },
    modules: {
        atlas,
        tipsAlert: {},
        expire: {},
        rollMsg: {},
    },
    mutations: {
        setInfoMap(state, { activityId, info }) {
            Vue.set(state.infoMap, activityId, info);
        },
        setCityListMap(state, { activityId, list }) {
            Vue.set(state.cityListMap, activityId, list);
        },
        setDreamIsNew(state, {is_new}) {
            state.isNew = is_new;
        },
        setCityDetailMap(state, { cityId, detail }: { cityId: number, detail: Dream.CityDetail }) {
            Vue.set(state.cityDetailMap, cityId, detail);
            detail.landmarks.forEach((ele) => {
                Vue.set(state.landmarkMap, ele.id, ele);
            });
        },
        setLandmarkDetailMap(state, { landmarkId, detail }) {
            Vue.set(state.landmarkDetailMap, landmarkId, detail);
        },
        /**
         * 消息列表
         */
        concatMessageList(state, { list }) {
            state.messageList.list = state.messageList.list.concat(list);
        },
        setMessageListLoaded(state, { loaded }) {
            state.messageList.loaded = loaded;
        },
        setMessageListReq(state, { req }) {
            state.messageList.requesting = req;
        },
        /*
        * 消息列表
        */
        initParticipationList(state, activityId) {
            Vue.set(state.participationList, activityId, {
                list: [],
                requesting: false,
                loaded: false,
            });
        },
        concatParticipationList(state, { activityId, list }) {
            state.participationList[activityId].list = state.participationList[activityId].list.concat(list);
        },
        setParticipationListLoaded(state, { activityId, loaded }) {
            state.participationList[activityId].loaded = loaded;
        },
        setParticipationListReq(state, { activityId, req }) {
            state.participationList[activityId].requesting = req;
        },

        setEnergy(state, energy) {
            state.energy = Number(energy);
        },

        /**
         * 地标排行
         */
        concatLandmarkRank(state, { list }) {
            state.landmarkRank.list = state.landmarkRank.list.concat(list);
        },
        setLandmarkRankSelf(state, { self }) {
            state.landmarkRank.self = self;
        },
        setLandmarkRankLoaded(state, { loaded }) {
            state.landmarkRank.loaded = loaded;
        },
        setLandmarkRankReq(state, { req }) {
            state.landmarkRank.requesting = req;
        },

        /**
         * 拼图排行
         */
        concatJigsawRank(state, { list }) {
            state.jigsawRank.list = state.jigsawRank.list.concat(list);
        },
        setJigsawRankSelf(state, { self }) {
            state.jigsawRank.self = self;
        },
        setJigsawRankLoaded(state, { loaded }) {
            state.jigsawRank.loaded = loaded;
        },
        setJigsawRankReq(state, { req }) {
            state.jigsawRank.requesting = req;
        },
        setFinishCount(state, finishCount) {
            state.finishCount = finishCount;
        },

        /**
         * 房车排行
         */
        concatTouringRank(state, { list }) {
            state.touringRank.list = state.touringRank.list.concat(list);
        },
        setTouringRankSelf(state, { self }) {
            state.touringRank.self = self;
        },
        setTouringRankLoaded(state, { loaded }) {
            state.touringRank.loaded = loaded;
        },
        setTouringRankReq(state, { req }) {
            state.touringRank.requesting = req;
        },
        /** 历史记录相关 */
        concatHistory(state, { list }) {
            state.history.list = state.history.list.concat(list);
        },
        setHistoryLoaded(state, { loaded }) {
            state.history.loaded = loaded;
        },
        setHistoryReq(state, { req }) {
            state.history.req = req;
        },

        setMissions(state, data) {
            state.missions = data;
        },
        setTipsAlert(state, data) {
            state.tipsAlert = data;
        },
        setExpire(state, expire) {
            state.expire = expire;
        },
        setRollMsg(state, rollMsg) {
            state.rollMsg = rollMsg;
        },
        setMedalCard(state, medalCard) {
            state.medalCard = medalCard;
        },
        setSpecialActivitySet(state, specialActive) {
            state.specialActive = specialActive;
        },

        setspecialTeam(state, { list }) {
            state.specialTeam.list = state.specialTeam.list.concat(list);
        },
        specialTeamReq(state, req) {
            state.specialTeam.req = req;
        },
        specialTeamLoaded(state, loaded) {
            state.specialTeam.loaded = loaded;
        },
        setSpecialOption(state, option) {
            state.specialOption = option;
        },
        setCurrentCity(state, currentCityID) {
            state.currentCityID = currentCityID;
        },
        setInviteInfo(state, { activityId, info }) {
            Vue.set(state.inviteInfoMap, activityId, info);
        },
        setCityMapFinishData(state, { data, id }) {
            Vue.set(state.cityMapFinishData, id, data);
        },
        setImgloadMap(state, { key, map }) {
            Vue.set(state.imgloadMap, key, map);
        },
        setNoticeSwitch(state, data) {
            if (data) {
                state.noticeSwitch = data;
            }
        },
    },
    actions: {
        /**
         * 获取信息
         */
        async getInfo(context, { activityId }) {
            const response = await Vue.axios.get('/api/v1/dream/info', {
                params: {
                    activity_id: activityId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setInfoMap', {
                    activityId,
                    detail: rdata.data,
                });
            }
            return rdata;
        },
        /**
         * 获取城市列表
         */
        async getCityList(context, { activityId }) {
            const response = await Vue.axios.get('/api/v1/dream/city/list', {
                params: {
                    activity_id: activityId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setCityListMap', {
                    activityId,
                    list: rdata.data,
                });
            }
            return rdata;
        },
        /**
         * 是否是新人
         */
        async getDreamIsNew(context, { activityId }) {
            const response = await Vue.axios.get('/api/v1/dream/is_new', {
                params: {
                    activity_id: activityId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setDreamIsNew', rdata.data);
            }
            return rdata;
        },
        /**
         * 大地图进度条
         */
        async getFinishCount(context, { activityId }) {
            const response = await Vue.axios.get('/api/v1/dream/finish_count', {
                params: {
                    activity_id: activityId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setFinishCount', rdata.data);
            }
            return rdata;
        },
        /**
         * 提示弹窗
         */
        async getTipsAlert(context, { activityId }) {
            const response = await Vue.axios.get('/api/v1/dream/tips_alert', {
                params: {
                    activity_id: activityId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setTipsAlert', rdata.data);
            }
            return rdata;
        },
        /**
         * 过期弹窗
         */
        async getExpire(context, { activityId }) {
            const date = getSessionStorage('isReq') || 0;
            if (+new Date() - +date < 86400000) {
                context.commit('setExpire', undefined);
                return;
            }
            const response = await Vue.axios.get('/api/v1/dream/get_expire', {
                params: {
                    activity_id: activityId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setExpire', rdata.data);
            }
            return rdata;
        },
        /**
         * 滚动提示
         */
        async getRollMsg(context, { activityId }) {
            const response = await Vue.axios.get('/api/v1/dream/roll_msg', {
                params: {
                    activity_id: activityId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setRollMsg', rdata.data);
            }
            return rdata;
        },
        /**
         * 重置大地图
         */
        async resetMap(context, {activityId} ) {
            const response = await Vue.axios.post('/api/v1/dream/reset', {
                activity_id: activityId,
            });
            const rdata = response.data;
            return rdata;
        },
        /**
         * 补玩重置
         */
        async resetCity(context, {cityId} ) {
            const response = await Vue.axios.post('/api/v1/dream/reset_city', {
                city_id: cityId,
            });
            const rdata = response.data;
            return rdata;
        },
        /**
         * 获取城市详情
         */
        async getCityDetail(context, { cityId }) {
            const response = await Vue.axios.get('/api/v1/dream/city/detail', {
                params: {
                    city_id: cityId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setCityDetailMap', {
                    cityId,
                    detail: new CityDetailModel(rdata.data),
                });
            }
            return rdata;
        },
        /**
         * 获取地标详情
         */
        async getLandmarkDetail(context, { landmarkId }) {
            const response = await Vue.axios.get('/api/v1/dream/landmark/detail', {
                params: {
                    landmark_id: landmarkId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setLandmarkDetailMap', {
                    landmarkId,
                    detail: rdata.data,
                });
            }
            return rdata;
        },
        /**
         * 获取通知
         */
        async getMessage({ state, commit }, { offset, count, forceUpdate }) {
            const wrap = state.messageList;
            if (wrap.requesting || (wrap.loaded && !forceUpdate)) {
                return;
            }
            if (forceUpdate) {
                wrap.list = [];
                wrap.loaded = false;
            }
            const end = offset + count;
            const len = wrap.list.length;
            if (end <= len) {
                return;
            }
            if (offset < len) {
                offset = len;
                count = end - offset;
            }
            commit('setMessageListReq', { req: true });
            const response = await Vue.axios.get('/api/v1/dream/message', {
                params: {
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const list = rdata.data;
                commit('concatMessageList', { list });
                if (list.length < count) {
                    commit('setMessageListLoaded', { loaded: true });
                }
            }
            commit('setMessageListReq', { req: false });
            return rdata;
        },
        /**
         * 获取报名列表
         */
        async getParticipation({ state, commit }, { activityId, offset, count, forceUpdate }) {
            let wrap = state.participationList[activityId];
            if (!wrap) {
                commit('initParticipationList', activityId);
                wrap = state.participationList[activityId];
            }
            if (wrap.requesting || (wrap.loaded && !forceUpdate)) {
                return;
            }
            if (forceUpdate) {
                wrap.list = [];
                wrap.loaded = false;
            }
            const end = offset + count;
            const len = wrap.list.length;
            if (end <= len) {
                return;
            }
            if (offset < len) {
                offset = len;
                count = end - offset;
            }
            commit('setParticipationListReq', { activityId, req: true });
            const response = await Vue.axios.get('/api/v1/dream/participation_list', {
                params: {
                    activity_id: activityId,
                    offset,
                    count,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const list = rdata.data;
                commit('concatParticipationList', { activityId, list });
                if (list.length < count) {
                    commit('setParticipationListLoaded', { activityId, loaded: true });
                }
            }
            commit('setParticipationListReq', { activityId, req: false });
            return rdata;
        },
        /**
         * 获取能量
         */
        async getEnergy(context) {
            const response = await Vue.axios.get('/api/v1/dream/energy');
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setEnergy', rdata.data?.energy || 0);
            }
            return rdata;
        },
        /**
         * 前进
         */
        async go(context, { activityId, cityId }) {
            const response = await Vue.axios.post('/api/v1/dream/go', {
                activity_id: activityId,
                city_id: cityId,
            });
            const rdata = response.data;
            return rdata;
        },
        /**
         * 设置目标
         */
        async setTarget(context, { activityId, target }) {
            const response = await Vue.axios.post('/api/v1/dream/target', {
                activity_id: activityId,
                target,
            });
            const rdata = response.data;
            return rdata;
        },

        /**
         * 获取地标排行
         */
        async getLandmarkRank({ state, commit }, { offset = 0, count = 15, type = '', specialId = '', forceUpdate = false } = {}) {
            const wrap = state.landmarkRank;
            if (offset === 0 && forceUpdate) {
                wrap.list = [];
                wrap.loaded = wrap.requesting = false;
                wrap.self = null;
            }
            if (wrap.requesting || wrap.loaded) {
                return;
            }
            const end = offset + count;
            const len = wrap.list.length;
            if (end <= len) {
                return;
            }
            commit('setLandmarkRankReq', { req: true });
            const response = await Vue.axios.get('/api/v1/dream/landmark_rank', {
                params: {
                    offset,
                    count,
                    type,
                    special_id: specialId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const data = rdata.data;
                commit('concatLandmarkRank', { list: data.rank || [] });
                commit('setLandmarkRankSelf', { self: data.self || null });
                if ((data.rank || []).length < count) {
                    commit('setLandmarkRankLoaded', { loaded: true });
                }
            }
            commit('setLandmarkRankReq', { req: false });
            return rdata;
        },

        /**
         * 获取拼图排行
         */
        async getJigsawRank({ state, commit }, { offset = 0, count = 15, type = '', specialId = '', forceUpdate = false } = {}) {
            const wrap = state.jigsawRank;
            if (offset === 0 && forceUpdate) {
                wrap.list = [];
                wrap.loaded = wrap.requesting = false;
                wrap.self = null;
            }
            if (wrap.requesting || wrap.loaded) {
                return;
            }
            const end = offset + count;
            const len = wrap.list.length;
            if (end <= len) {
                return;
            }
            commit('setJigsawRankReq', { req: true });
            const response = await Vue.axios.get('/api/v1/dream/jigsaw_rank', {
                params: {
                    offset,
                    count,
                    type,
                    special_id: specialId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const data = rdata.data;
                commit('concatJigsawRank', { list: data.rank || [] });
                commit('setJigsawRankSelf', { self: data.self || null });
                if ((data.rank || []).length < count) {
                    commit('setJigsawRankLoaded', { loaded: true });
                }
            }
            commit('setJigsawRankReq', { req: false });
            return rdata;
        },

        /**
         * 获取房车排行
         */
        async getTouringRank({ state, commit }, { offset = 0, count = 15, type = '', specialId = '', forceUpdate = false } = {}) {
            const wrap = state.touringRank;
            if (offset === 0 && forceUpdate) {
                wrap.list = [];
                wrap.loaded = wrap.requesting = false;
                wrap.self = null;
            }
            if (wrap.requesting || wrap.loaded) {
                return;
            }
            const end = offset + count;
            const len = wrap.list.length;
            if (end <= len) {
                return;
            }
            commit('setTouringRankReq', { req: true });
            const response = await Vue.axios.get('/api/v1/dream/touring_rank', {
                params: {
                    offset,
                    count,
                    type,
                    special_id: specialId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const data = rdata.data;
                commit('concatTouringRank', { list: data.rank || [] });
                commit('setTouringRankSelf', { self: data.self || null });
                if ((data.rank || []).length < count) {
                    commit('setTouringRankLoaded', { loaded: true });
                }
            }
            commit('setTouringRankReq', { req: false });
            return rdata;
        },

        /**
         * 获取每日任务弹窗内容
         */
        async getMissions(context) {
            const response = await Vue.axios.get('/api/v1/dream/mission');
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setMissions', rdata.data);
            }
            return rdata;
        },

        /**
         * 领取每日任务奖励
         */
        async getFragment(context, type: Dream.MissionType) {
            const response = await Vue.axios.post('/api/v1/dream/receive', {
                type,
            });
            const rdata = response.data;
            return rdata;
        },
        /**
         * 获取地标卡
         */
        async getLandmarkCard(context, landmark_id) {
            const response = await Vue.axios.get('/api/v1/dream/landmark_card', {
                params: { landmark_id },
            });
            const rdata = response.data;
            return rdata;
        },
        /*
         * 获取历史记录
         */
        async getHistory({ state, commit }, { offset, count, activity_id, forceUpdate }) {
            const wrap = state.history;
            if (forceUpdate && offset === 0) {
                wrap.list = [];
                wrap.loaded = wrap.req = false;
            }
            if (wrap.req || wrap.loaded) {
                return;
            }
            const end = offset + count;
            const len = wrap.list.length;
            if (end <= len) {
                return;
            }
            commit('setHistoryReq', { req: true });
            const response = await Vue.axios.get('/api/v1/dream/history', {
                params: {
                    offset,
                    count,
                    activity_id,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                const list = rdata.data || [];
                commit('concatHistory', { list });
                if (list.length < count) {
                    commit('setHistoryLoaded', { loaded: true });
                }
            }
            commit('setHistoryReq', { req: false });
            return rdata;
        },
        /**
         * 获取城市完成卡
         */
        async getFinishCityCard(context, cityId) {
            const response = await Vue.axios.get('/api/v1/dream/finish_city_card', {
                params: { city_id: cityId },
            });
            const rdata = response.data;
            return rdata;
        },
        /*
         * 获取路线详情
         * 图鉴id
         */
        async getRouteDetail(context, id) {
            const response = await Vue.axios.get('/api/v1/dream/city_detail', {
                params: { id },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setCityDetail', rdata.data);
            }
            return rdata;
        },
        /*
         * 获取奖牌卡
         */
        async getMedalCard(context, { activityId }) {
            const response = await Vue.axios.get('/api/v1/dream/medal_card', {
                params: {
                    activity_id: activityId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setMedalCard', rdata.data);
            }
            return rdata;
        },
        /*
         * 获取从首页进入
         */
        async addHomeLog(context) {
            const response = await Vue.axios.post('/api/v1/dream/add_home_log');
            const rdata = response.data;
            return rdata;
        },
        /* * 获取每日单次打卡5公里奖励碎片弹窗
         * 图鉴id
         */
        async getFinishDistanceTips(context, activity_id) {
            const response = await Vue.axios.get('/api/v1/dream/finish_distance_tips', {
                params: { activity_id },
            });
            const rdata = response.data;
            return rdata;
        },
        /* * 专题活动页面图片设置
         */
        async getSpecialActivitySet(context, { activity_id, special_id }) {
            const response = await Vue.axios.get('/api/v1/dream/special_activity_set', {
                params: { activity_id, special_id },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setSpecialActivitySet', rdata.data);
            }
            return rdata;
        },
        /* * 专题队伍列表
         */
        async getSpecialTeamList({ state, commit }, { activity_id, specialId, offset, count, search, forceUpdate }) {
            let wrap = state.specialTeam;
            if (forceUpdate && offset === 0) {
                wrap.list = [];
                wrap.req = false;
                wrap.loaded = false;
            }
            wrap = state.specialTeam;
            if (wrap.loaded || wrap.req) {
                return;
            }
            commit('specialTeamReq', true);
            const response = await Vue.axios.get('/api/v1/dream/special_team_list', {
                params: { activity_id, offset, count, search, special_id: specialId },
            });
            commit('specialTeamReq', false);
            const rdata = response.data;
            if (rdata.status === 0) {
                const list = rdata.data;
                commit('setspecialTeam', { list });
                if (list.length < count) {
                    commit('specialTeamLoaded', true);
                }
            }
            return rdata;
        },
        async getCurrentCity(context, { activityId }) {
            const response = await Vue.axios.get('/api/v1/dream/get_current_city', {
                params: {
                    activity_id: activityId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setCurrentCity', rdata.data.id);
            }
            return rdata;
        },
        /** 获取城市地图成功弹窗数据 */
        async getCityMapFinishData({ state, commit }, { cityId }) {
            if (state.cityMapFinishData[cityId]) {
                return;
            }
            const response = await Vue.axios.get('/api/v1/dream/get_finish_share', {
                params: {
                    city_id: cityId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                commit('setCityMapFinishData', { data: rdata.data, id: cityId });
            }
            return rdata;
        },
        /** 获取补偿弹窗内容 */
        async getCompensate(context) {
            const response = await Vue.axios.get('/api/v1/dream/get_compensate');
            const rdata = response.data as {date: string; city: string};
            return rdata;
        },
        /** 首次点击地标后调用 */
        async changeIsNew(context) {
            const response = await Vue.axios.post('/api/v1/dream/change_is_new');
            const rdata = response.data;
            return rdata;
        },
        /**
         * 获取邀请活动信息
         */
        async getInviteInfo(context, { activityId, specialId, forceUpdate = false }) {
            if (!forceUpdate && context.state.inviteInfoMap[activityId]) {
                return {
                    status: 0,
                    data: context.state.inviteInfoMap[activityId],
                };
            }
            const response = await Vue.axios.get('/api/v1/dream/invite/info', {
                params: {
                    activity_id: activityId,
                    special_id: specialId,
                },
            });
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setInviteInfo', {
                    activityId,
                    info: rdata.data,
                });
            }
            return rdata;
        },

        /**
         * 获取消息开关
         */
        async getNoticeSwitch(context, { forceUpdate = false }) {
            if (!forceUpdate) {
                return {
                    status: 0,
                    data: context.state.noticeSwitch,
                };
            }
            const response = await Vue.axios.get('/api/v1/dream/notice_switch');
            const rdata = response.data;
            if (rdata.status === 0) {
                context.commit('setNoticeSwitch', rdata.data);
            }
            return rdata;
        },

        /**
         * 开启/关闭消息
         */
        async setNoticeSwitch(context, { key, value }) {
            const response = await Vue.axios.post('/api/v1/dream/notice_status', {
                [key]: value,
            });
            const rdata = response.data;
            return rdata;
        },
        /**
         * 获取icl详情
         * Todo：该接口跟活动详情返回的icl_image一致但需要用该接口来记录访问人数
         */
        async getIcl(context, activity_id) {
            const response = await Vue.axios.get('/api/v1/dream/get_icl', {
                params: { activity_id },
            });
            const rdata = response.data;
            return rdata;
        },
        /**
         * 保存icl-10秒后浏览数
         */
        async addIclPv(context, activity_id) {
            const response = await Vue.axios.post('/api/v1/dream/add_icl_pv', {
                activity_id,
            });
            const rdata = response.data;
            return rdata;
        },
    },
};

export default m;
